import { isBrowser, isTest } from '@/utils/env';
import LayoverClient from '@/logging/LayoverClient';
import LayoverDefaultClient from '@/logging/LayoverDefaultClient';
import LayoverNoopClient from '@/logging/LayoverNoopClient';

import { v4 as uuidV4 } from 'uuid';
import moment from 'moment';

type EventData = object;
type EventDataGetter = EventData | (() => EventData);
type LoggableError = Error | string;

type LogEntry = {
  eventName: string; // Short description of the event that occurred
  eventData: EventData; // Metadata about the event
  eventNum: number; // Number used to order the events for a page load
  eventId: string; // ID which is generated for each event (helps with de-duping)
  appLoadId: string; // ID assigned when the webapp loads
  pageLoadId: string; // ID changed on every page transition
  pageUrl: any; // URL of the page when the event was logged
  clientTimestamp: string; // Time when the event occurred on the client (ISO 8601, UTC)
};

export const MAX_TOP_OF_STACK_CHARS = 1000; // Number of characters to log of the stack
export const MAX_TOP_OF_STACK_LINES = 15; // Number of lines log of the stack

// Events which should not be logged
export const EVENT_NAME_BLOCK_LIST = [
  /^\s*$/, // Empty strings and whitespace-only
  /^error.ResizeObserver\b/, // Error thrown by ResizeObservers that have no user impact
];

/**
 * Layover Logger
 *
 * This class collects and transports logs from the browser to datadog through the Layover service.
 * See: /online/layover/src/routes/log-entries/index.test.js
 *
 */
export default class LayoverLogger {
  _appLoadId: string; // UUID that is unique for every load of the app (every new tab, page refresh, etc.)
  _client: LayoverClient;
  _nextEventNum: number; // Incrementing number that allows events to be sorted on the server side without relying on timestamps
  _pageLoadId: string; // UUID that is unique for every page (different pageLoadId's can have the same appLoadId)

  constructor({ client }: { client: LayoverClient }) {
    this._client = client;
    this._nextEventNum = 1;

    this._appLoadId = uuidV4();
    this._pageLoadId = uuidV4();
    window.addEventListener('popstate', this._onUrlChange);
  }

  getAppLoadId(): string {
    return this._appLoadId;
  }

  getPageLoadId(): string {
    return this._pageLoadId;
  }

  _mkLogEntry(eventName: string, eventData: object): LogEntry {
    return {
      eventName,
      eventData,
      eventNum: this._nextEventNum++,
      eventId: uuidV4(),
      appLoadId: this.getAppLoadId(),
      pageLoadId: this.getPageLoadId(),
      pageUrl: this._getPageUrl(),
      clientTimestamp: moment().toISOString(),
    };
  }

  // Eventually send a log entry to the server
  log(eventName: string, eventData: EventDataGetter): boolean {
    if (isEventNameBlocked(eventName)) {
      return false;
    }
    const entry = this._mkLogEntry(eventName, this._getLogEntryData(eventData));
    return this._client.send(entry);
  }

  // Send a log entry as fast as possible
  logVital(eventName: string, eventData: EventDataGetter): boolean {
    if (isEventNameBlocked(eventName)) {
      return false;
    }
    const entry = this._mkLogEntry(eventName, this._getLogEntryData(eventData));
    return this._client.sendNow(entry);
  }

  // Parse an error object (or similar) and send as vital log
  async logError(error?: LoggableError, extraProps?: object): Promise<boolean> {
    const errorData = await this.buildEventDataForError(error);
    const eventData: any = {
      ...errorData,
      ...(extraProps || {}),
    };
    return this.logVital(eventData.eventName as string, eventData as object);
  }

  // Same as #logError() but records under specified error name for event
  async logNamedError(
    errorName: string,
    error?: LoggableError,
    extraProps?: object,
  ): Promise<boolean> {
    const errorData = await this.buildEventDataForError(error);
    const eventData = {
      ...errorData,
      ...(extraProps || {}),
      eventName: errorName,
    };
    return this.logVital(errorName, eventData);
  }

  async buildEventDataForError(error?: LoggableError): Promise<object> {
    const isTypeMissing = typeof error === 'string' && !/\w:\W/.test(error);
    const errorType = isTypeMissing
      ? 'UnknownError'
      : this._parseErrorType(error);
    const eventName = `error.${errorType}`;
    let message: string | null = null;
    let topOfStack: string | null = null;
    if (error instanceof Error) {
      message = error.message;
      // TODO: This stack may be minified. Need to get a stack parser to be useful.
      const stackLines = error.stack?.split('\n') || [];
      topOfStack = stackLines // Limit the size of the logs by removing some formatting
        .slice(0, MAX_TOP_OF_STACK_LINES) // Max lines
        .map((_) => _.replace(/^\s+at /, '')) // Remove '   at ' in the beginning of line
        .join('\n')
        .slice(0, MAX_TOP_OF_STACK_CHARS);
    } else if (typeof error === 'string') {
      message = error;
    } else {
      message = Object.prototype.toString.call(error ?? '');
    }
    return {
      eventName,
      errorType,
      message,
      topOfStack,
    };
  }

  // Handle browser's ErrorEvent (from window.onerror, websocket.onerror, or image.onerror)
  async logErrorEvent(
    event: ErrorEvent,
    extraProps?: object,
  ): Promise<boolean> {
    extraProps = {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      type: event.type,
      ...(extraProps || {}),
    };
    if (event.error instanceof Error) {
      return this.logError(event.error, extraProps);
    }
    const errorType = this._parseErrorType(event.message);
    const eventData = {
      eventName: `error.${errorType}`,
      errorType,
      message: event.message,
      ...extraProps,
    };
    return this.logVital(eventData.eventName, eventData);
  }

  // Determine the type of error passed to it
  _parseErrorType(error?: LoggableError): string {
    let errorMsg = 'UnknownError';
    if (typeof error === 'string') {
      errorMsg = error;
    } else if (error instanceof Error) {
      errorMsg = (error.stack || '').split('\n')[0];
    }
    errorMsg = errorMsg.replace(/^Uncaught /, '');
    const type = errorMsg.split(':')[0];
    return type;
  }

  // Safely get the log data, or log an error to allow for debugging
  _getLogEntryData(getter: EventDataGetter): object {
    if (typeof getter === 'function') {
      try {
        return getter();
      } catch (error: any) {
        console.error(error);
        return {
          // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
          __type: (error?.constructor?.name || '') + error,
          __message: error.message,
          __stack: error.stack,
        };
      }
    }
    return getter;
  }

  // Change to a new page, change the pageLoadId
  _onUrlChange = (): void => {
    this._pageLoadId = uuidV4();
  };

  // Build an object based on the URL
  _getPageUrl(): URL | null {
    if (typeof window !== 'undefined') {
      return new URL(window.location.href);
    }
    return null;
  }
}

let inst: LayoverLogger | null = null;
export function getLayoverLogger(): LayoverLogger {
  if (!inst) {
    const args = {
      destinationUrl: '/beacon/logs',
    };
    const shouldSendLogs = isBrowser() && !isTest();
    const client = shouldSendLogs
      ? new LayoverDefaultClient(args)
      : new LayoverNoopClient();
    inst = new LayoverLogger({ client });
  }
  return inst;
}

export function isEventNameBlocked(eventName: string): boolean {
  for (const regex of EVENT_NAME_BLOCK_LIST) {
    if (regex.test(eventName)) {
      return true;
    }
  }
  return false;
}
